import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Tooltip,
  Collapse,
  Chip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Search as SearchIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminService } from '../../../services/adminService';
import SchoolSearch from './SchoolSearch';
import BulkTeamUpload from './BulkTeamUpload';
import SchoolsTable from './SchoolsTable';


const TeamManagementPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [activeTeam, setActiveTeam] = useState(null);
  const [schoolSearchOpen, setSchoolSearchOpen] = useState(false);
  const [teamFormOpen, setTeamFormOpen] = useState(false);
  const [teamFormData, setTeamFormData] = useState({ name: '', funder: '' });
  const [editingTeam, setEditingTeam] = useState(null);
  const [error, setError] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 50,
    total: 0,
    totalPages: 1
  });
  const [hasMoreTeams, setHasMoreTeams] = useState(true);
  const [teamUsersCache, setTeamUsersCache] = useState({});
  const [loadingTeamUsers, setLoadingTeamUsers] = useState({});
  const [exportingCSV, setExportingCSV] = useState(false);

  // Debounce search query to improve performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Filtered teams for search - uses debounced query for better performance
  const filteredTeams = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return teams;
    const query = debouncedSearchQuery.toLowerCase().trim();
    return teams.filter(team => 
      (team.name && team.name.toLowerCase().includes(query)) || 
      (team.funder && team.funder.toLowerCase().includes(query))
    );
  }, [teams, debouncedSearchQuery]);

  // Load teams from database
  const loadTeams = useCallback(async (page = 0, pageSize = 25, appendToExisting = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminService.listTeams(page, pageSize, enqueueSnackbar, false);
      
      // Handle both paginated and non-paginated responses
      if (response.teams && response.pagination) {
        if (appendToExisting) {
          setTeams(prevTeams => [...prevTeams, ...response.teams]);
        } else {
          setTeams(response.teams);
        }
        setPagination(response.pagination);
        setHasMoreTeams(response.teams.length === pageSize && response.pagination.page < response.pagination.totalPages - 1);
      } else if (Array.isArray(response)) {
        if (appendToExisting) {
          setTeams(prevTeams => [...prevTeams, ...response]);
        } else {
          setTeams(response);
        }
        setPagination({
          page: 0,
          pageSize: response.length,
          total: response.length,
          totalPages: 1
        });
        setHasMoreTeams(false);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      setError('Failed to load teams. Please try again.');
      enqueueSnackbar('Error loading teams', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Load team schools
  const loadTeamSchools = useCallback(async (teamId) => {
    try {
      // This will use the new database route to get schools for a team
      const response = await adminService.getTeamSchools?.(teamId, enqueueSnackbar);
      return response?.schools || [];
    } catch (error) {
      console.error('Error loading team schools:', error);
      return [];
    }
  }, [enqueueSnackbar]);

  // Load more teams
  const loadMoreTeams = useCallback(async () => {
    if (!hasMoreTeams || loading) return;
    await loadTeams(pagination.page + 1, pagination.pageSize, true);
  }, [hasMoreTeams, loading, loadTeams, pagination.page, pagination.pageSize]);

  // Initial load - load first 50 teams for optimal performance and usability balance
  useEffect(() => {
    loadTeams(0, 50, false); // Load first 50 teams initially
  }, [loadTeams]);

  // Handle team creation
  const handleCreateTeam = async (formData) => {
    try {
      setLoading(true);
      
      const teamData = {
        name: formData.name.trim(),
        funder: formData.funder?.trim() || null,
        school_uuids: formData.school_uuids || []
      };

      await adminService.createTeam(teamData, enqueueSnackbar);
      enqueueSnackbar('Team created successfully', { variant: 'success' });
      
      setTeamFormOpen(false);
      setTeamFormData({ name: '', funder: '' });
      await loadTeams(0, 50, false); // Refresh first page
    } catch (error) {
      console.error('Error creating team:', error);
      enqueueSnackbar('Failed to create team', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle team update
  const handleUpdateTeam = async (teamId, formData) => {
    try {
      setLoading(true);
      
      const updateData = {
        name: formData.name.trim(),
        funder: formData.funder?.trim() || null
      };

      await adminService.updateTeam(teamId, updateData, enqueueSnackbar);
      enqueueSnackbar('Team updated successfully', { variant: 'success' });
      
      setTeamFormOpen(false);
      setEditingTeam(null);
      setTeamFormData({ name: '', funder: '' });
      await loadTeams(0, 50, false); // Refresh first page
    } catch (error) {
      console.error('Error updating team:', error);
      enqueueSnackbar('Failed to update team', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle team deletion
  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await adminService.deleteTeam(teamId, enqueueSnackbar);
      enqueueSnackbar('Team deleted successfully', { variant: 'success' });
      await loadTeams(0, 50, false); // Refresh first page
    } catch (error) {
      console.error('Error deleting team:', error);
      enqueueSnackbar('Failed to delete team', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle adding schools to team
  const handleAddSchoolsToTeam = async (teamId, schoolUuids) => {
    try {
      setLoading(true);
      await adminService.addSchoolsToTeam(teamId, schoolUuids, enqueueSnackbar);
      enqueueSnackbar(`${schoolUuids.length} school(s) added to team`, { variant: 'success' });
      await loadTeams(0, 100, false); // Use false to replace teams list, not append
      
      // If this team is expanded, reload its schools
      if (expandedTeam === teamId) {
        const schools = await loadTeamSchools(teamId);
        setTeams(prev => prev.map(t => 
          t.id === teamId ? { ...t, schools, school_count: schools.length } : t
        ));
      }
    } catch (error) {
      console.error('Error adding schools to team:', error);
      enqueueSnackbar('Failed to add schools to team', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle removing school from team
  const handleRemoveSchoolFromTeam = async (teamId, schoolUuid) => {
    try {
      setLoading(true);
      await adminService.removeSchoolFromTeam(teamId, schoolUuid, enqueueSnackbar);
      enqueueSnackbar('School removed from team', { variant: 'success' });
      await loadTeams(0, 100, false); // Use false to replace teams list, not append
      
      // If this team is expanded, reload its schools
      if (expandedTeam === teamId) {
        const schools = await loadTeamSchools(teamId);
        setTeams(prev => prev.map(t => 
          t.id === teamId ? { ...t, schools, school_count: schools.length } : t
        ));
      }
    } catch (error) {
      console.error('Error removing school from team:', error);
      enqueueSnackbar('Failed to remove school from team', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle team expansion
  const handleTeamExpansion = async (teamId) => {
    if (expandedTeam === teamId) {
      setExpandedTeam(null);
    } else {
      setExpandedTeam(teamId);
      // Load schools for this team when expanded
      const team = teams.find(t => t.id === teamId);
      if (team && !team.schools) {
        const schools = await loadTeamSchools(teamId);
        setTeams(prev => prev.map(t => 
          t.id === teamId ? { ...t, schools, school_count: schools.length } : t
        ));
      }
      // Load users for this team when expanded (lazy, cached)
      loadTeamUsers(teamId);
    }
  };

  // Open team form for editing
  const openEditForm = (team) => {
    setEditingTeam(team);
    setTeamFormData({
      name: team.name,
      funder: team.funder || ''
    });
    setTeamFormOpen(true);
  };

  const exportTeamsToCSV = async () => {
    setExportingCSV(true);
    try {
      // Fetch schools for any team that hasn't been expanded yet
      const teamsWithSchools = await Promise.all(
        teams.map(async (t) => {
          if (t.schools) return t; // already loaded
          const schools = await loadTeamSchools(t.id);
          return { ...t, schools };
        })
      );

      const headers = ['Team ID', 'Team Name', 'Funder', 'School Count', 'User Count', 'Created Date', 'School Names'];
      const rows = teamsWithSchools.map(t => {
        const schoolNames = (t.schools || []).map(s => s.name || s.uuid || '').filter(Boolean).join('; ');
        return [
          t.id || '',
          t.name || '',
          t.funder || '',
          t.school_count || 0,
          t.user_count || 0,
          t.created_at ? new Date(t.created_at).toLocaleDateString() : '',
          schoolNames
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
      });

      const csvContent = [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teams_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting teams CSV:', err);
      enqueueSnackbar('Failed to export CSV', { variant: 'error' });
    } finally {
      setExportingCSV(false);
    }
  };

  const loadTeamUsers = useCallback(async (teamId) => {
    if (teamUsersCache[teamId] !== undefined) return;
    setLoadingTeamUsers(prev => ({ ...prev, [teamId]: true }));
    try {
      const users = await adminService.getTeamUsers(teamId, enqueueSnackbar);
      setTeamUsersCache(prev => ({ ...prev, [teamId]: users || [] }));
    } catch (err) {
      console.error('Error loading team users:', err);
      setTeamUsersCache(prev => ({ ...prev, [teamId]: [] }));
    } finally {
      setLoadingTeamUsers(prev => ({ ...prev, [teamId]: false }));
    }
  }, [teamUsersCache, enqueueSnackbar]);



  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
        <Button variant="contained" onClick={() => loadTeams(0, 100, false)}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Team Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setBulkUploadOpen(true)}
            startIcon={<AddIcon />}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setEditingTeam(null);
              setTeamFormData({ name: '', funder: '' });
              setTeamFormOpen(true);
            }}
            startIcon={<AddIcon />}
          >
            Create Team
          </Button>
        </Box>
      </Box>

      {/* Search and Export */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search teams by name or funder..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
        <Button
          variant="outlined"
          startIcon={exportingCSV ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={exportTeamsToCSV}
          disabled={teams.length === 0 || exportingCSV}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {exportingCSV ? 'Fetching schools…' : 'Export CSV'}
        </Button>
      </Box>

      {/* Teams Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team Name</TableCell>
              <TableCell>Funder</TableCell>
              <TableCell align="center">Schools</TableCell>
              <TableCell align="center">Users</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading teams...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredTeams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No teams match your search' : 'No teams found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTeams.map((team) => (
                <React.Fragment key={team.id}>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleTeamExpansion(team.id)}
                          disabled={loading}
                        >
                          {expandedTeam === team.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography variant="body2" fontWeight="medium">
                          {team.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {team.funder ? (
                        <Chip label={team.funder} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SchoolIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        <Typography variant="body2">{team.school_count || 0}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PeopleIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        <Typography variant="body2">{team.user_count || 0}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {team.created_at ? new Date(team.created_at).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit team">
                        <IconButton size="small" onClick={() => openEditForm(team)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add schools">
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setActiveTeam(team);
                            setSchoolSearchOpen(true);
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete team">
                        <IconButton size="small" onClick={() => handleDeleteTeam(team.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded row for team details */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0 }}>
                      <Collapse in={expandedTeam === team.id}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="h6" gutterBottom>
                            Team Details: {team.name}
                          </Typography>
                          
                          {/* Schools assigned to this team */}
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Assigned Schools ({team.school_count || 0})
                          </Typography>
                          
                          {expandedTeam === team.id && (
                            <SchoolsTable
                              schools={team.schools || []}
                              teamId={team.id}
                              onRemoveSchool={handleRemoveSchoolFromTeam}
                              maxHeight={300}
                              showPagination={team.schools && team.schools.length > 10}
                            />
                          )}

                          {/* Users on this team */}
                          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                            Users on this Team ({team.user_count || 0})
                          </Typography>
                          {expandedTeam === team.id && (
                            loadingTeamUsers[team.id] ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">Loading users...</Typography>
                              </Box>
                            ) : (
                              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Name</TableCell>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Email</TableCell>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Role</TableCell>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Last Login</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(teamUsersCache[team.id] || []).length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={4} align="center">
                                          <Typography variant="body2" color="text.secondary">No users on this team</Typography>
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      (teamUsersCache[team.id] || []).map(u => (
                                        <TableRow key={u.uid} hover>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {u.display_name || u.name || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">{u.role || '—'}</Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                              {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load More Button */}
      {hasMoreTeams && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <Button 
            variant="outlined" 
            onClick={loadMoreTeams}
            disabled={loading}
          >
            Load More Teams
          </Button>
        </Box>
      )}

      {/* Pagination Info */}
      {pagination.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {teams.length} of {pagination.total} teams
          </Typography>
        </Box>
      )}

      {/* Team Form Dialog */}
      <Dialog open={teamFormOpen} onClose={() => setTeamFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Create New Team'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={teamFormData.name}
                onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Funder"
                value={teamFormData.funder}
                onChange={(e) => setTeamFormData({ ...teamFormData, funder: e.target.value })}
                helperText="Optional: Organization or entity funding this team"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamFormOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              if (editingTeam) {
                handleUpdateTeam(editingTeam.id, teamFormData);
              } else {
                handleCreateTeam(teamFormData);
              }
            }}
            disabled={!teamFormData.name.trim() || loading}
          >
            {loading ? <CircularProgress size={24} /> : (editingTeam ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Only render SchoolSearch when dialog is open */}
      {schoolSearchOpen && (
        <SchoolSearch
          open={schoolSearchOpen}
          onClose={() => setSchoolSearchOpen(false)}
          onSchoolsSelected={(schools) => {
            if (activeTeam && schools.length > 0) {
              // Prefer UUID so the exact school is added (critical for split schools sharing one ncessch)
              const schoolIds = schools.map(school => school.uuid || school.ncessch);
              handleAddSchoolsToTeam(activeTeam.id, schoolIds);
            }
            setSchoolSearchOpen(false);
            setActiveTeam(null);
          }}
          title={`Add Schools to ${activeTeam?.name || 'Team'}`}
        />
      )}

      {/* Only render BulkTeamUpload when dialog is open */}
      {bulkUploadOpen && (
        <BulkTeamUpload
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onSuccess={() => {
            setBulkUploadOpen(false);
            loadTeams(0, 25, false); // Refresh first page
          }}
        />
      )}
    </Box>
  );
};

export default React.memo(TeamManagementPanel);